// ===========================================
// HOOK: Auto-delegación AFIP + Letrita A (ORR-111 Milestone 4/5)
// ===========================================
//
// - Query: listar servicios delegados de un cliente (pre-deselect en dialog)
// - Query: listar progress por jobId (polling Background Function)
// - Mutation: iniciarAutoDelegacion → valida y encola la Background Function con
//   un jobId que genera el cliente. El diálogo hace polling de ese jobId y, cuando
//   la fila `job` llega a un estado terminal, invalida servicios y clientes.

import { useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { toast } from 'sonner'

import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers'
import { usePermissions } from '@/hooks/usePermissions'
import { assertPermiso, handleMutationError } from '@/lib/permisos-guard'
import {
  afipDelegacionService,
  type IniciarAutoDelegacionInput,
  type IniciarAutoDelegacionResult,
} from '@/services/afipDelegacionService'

// ===========================================
// Query Keys
// ===========================================

export const afipDelegacionKeys = {
  all: ['afip-delegacion'] as const,
  serviciosDelegados: (clienteId: string, organizacionId: string) =>
    [...afipDelegacionKeys.all, 'servicios', clienteId, organizacionId] as const,
  progress: (jobId: string) => [...afipDelegacionKeys.all, 'progress', jobId] as const,
  representante: (organizacionId: string) =>
    [...afipDelegacionKeys.all, 'representante', organizacionId] as const,
}

// ===========================================
// Queries
// ===========================================

export function useServiciosDelegadosCliente(clienteId: string | null | undefined) {
  const { supabase } = useSupabase()
  const { membresia } = useOrganizacion()
  const organizacionId = membresia?.organizacion_id ?? ''

  return useQuery({
    queryKey: afipDelegacionKeys.serviciosDelegados(clienteId ?? '', organizacionId),
    queryFn: () =>
      afipDelegacionService.listarServiciosDelegados(supabase!, clienteId!, organizacionId),
    enabled: !!supabase && !!clienteId && !!organizacionId,
    staleTime: 60_000,
  })
}

/** CUIT of the current representative of auto-delegation. The query runs only with `enabled`. */
export function useRepresentanteAfip(enabled: boolean) {
  const { getAccessTokenSilently } = useAuth0()
  const { membresia } = useOrganizacion()
  const organizacionId = membresia?.organizacion_id ?? ''

  return useQuery({
    queryKey: afipDelegacionKeys.representante(organizacionId),
    queryFn: async () =>
      afipDelegacionService.obtenerRepresentante(await getAccessTokenSilently(), organizacionId),
    enabled: enabled && !!organizacionId,
    staleTime: 10 * 60_000,
    retry: false,
  })
}

/**
 * Progress of one auto-delegation run. With `polling`, refetches every 3 s until the `job` row
 * leaves `en_curso`: from then on nothing else changes the result.
 */
export function useProgressAutoDelegacion(
  jobId: string | null | undefined,
  { polling = false }: { polling?: boolean } = {},
) {
  const { supabase } = useSupabase()
  const { membresia } = useOrganizacion()
  const organizacionId = membresia?.organizacion_id ?? ''

  return useQuery({
    queryKey: afipDelegacionKeys.progress(jobId ?? ''),
    queryFn: () => afipDelegacionService.listarProgress(supabase!, jobId!, organizacionId),
    enabled: !!supabase && !!jobId && !!organizacionId,
    refetchInterval: (query) => {
      if (!polling) return false
      const marcador = query.state.data?.find((f) => f.step === 'job')
      return marcador && marcador.status !== 'en_curso' ? false : 3_000
    },
    staleTime: 2_000,
  })
}

/** Refreshes what a finished auto-delegation run changes: delegated services and the client. */
export function useRefrescarTrasAutoDelegacion() {
  const queryClient = useQueryClient()

  return useCallback(
    (clienteId: string, organizacionId: string) => {
      queryClient.invalidateQueries({
        queryKey: afipDelegacionKeys.serviciosDelegados(clienteId, organizacionId),
      })
      // La raíz `clientes` cubre la ficha (`clientesKeys.detail`).
      queryClient.invalidateQueries({ queryKey: ['clientes'] })
    },
    [queryClient],
  )
}

// ===========================================
// Mutation: iniciar auto-delegación
// ===========================================

interface IniciarAutoDelegacionVars {
  pin: string
  input: IniciarAutoDelegacionInput
  clienteNombre: string
}

export function useIniciarAutoDelegacion() {
  const { getAccessTokenSilently } = useAuth0()
  const { puede } = usePermissions()

  return useMutation<IniciarAutoDelegacionResult, Error, IniciarAutoDelegacionVars>({
    mutationFn: async ({ pin, input }) => {
      assertPermiso(puede, 'autoDelegarAfip')
      const token = await getAccessTokenSilently()
      return afipDelegacionService.iniciarAutoDelegacion(token, pin, input)
    },
    onSuccess: (result) => {
      toast.success(result.message)
    },
    onError: (error) => {
      handleMutationError(error, 'Error al iniciar auto-delegación AFIP')
    },
  })
}
